/**
 * contato.ts — Categoria "Contato" (10 modelos premium)
 *
 * Telefone, e-mail, endereço e horários apresentados em seções com camadas,
 * com variações claras, escuras, com foto e com convite.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

const DADOS = 'Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br\nEndereço|Av. Paulista, 1000 — São Paulo/SP';
const HORARIOS = 'Segunda a sexta|8h às 18h\nSábado|9h às 13h\nDomingo|Plantão por WhatsApp';

/** Atalho para o bloco de contato. */
function contato(itens: string, colunas = '3', titulo = '') {
  return b('page.contact', { title: titulo, items: itens, columns: colunas });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'contato.tres-colunas',
    category: 'contato',
    label: 'Contato em três colunas',
    description: 'Telefone, e-mail e endereço lado a lado.',
    preview: 'grid-3',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '1080px', motion: 'fade' }, [contato(DADOS, '3', 'Fale com a gente')]),
    ],
  },
  {
    key: 'contato.horarios',
    category: 'contato',
    label: 'Horários de atendimento',
    description: 'Quadro de horários por dia da semana.',
    preview: 'grid-3',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '900px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        contato(HORARIOS, '3', 'Quando atendemos'),
      ]),
    ],
  },
  {
    key: 'contato.com-foto',
    category: 'contato',
    label: 'Contato com foto',
    description: 'Foto do escritório ao lado dos dados.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { bleed: 'contained', layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade' }, [
        b('ui.title', { text: 'Onde estamos', size: 'lg', level: 'h2' }),
        contato(DADOS, '1'),
        b('ui.image', { src: FOTO.escritorio, alt: 'Fachada do escritório', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
      ]),
    ],
  },
  {
    key: 'contato.escuro',
    category: 'contato',
    label: 'Contato em fundo escuro',
    description: 'Bloco escuro com brilho e dados em destaque.',
    preview: 'grid-3',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '1080px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade-up' },
        [b('ui.title', { text: 'Fale com a gente', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }), contato(DADOS)],
      ),
    ],
  },
  {
    key: 'contato.com-convite',
    category: 'contato',
    label: 'Contato com convite',
    description: 'Dados de contato e botão para atendimento imediato.',
    preview: 'cta',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '1000px', motion: 'fade' }, [
        contato(DADOS, '3', 'Escolha como prefere falar'),
        b('ui.buttons', { items: 'Chamar no WhatsApp|#contato|solid\nEnviar e-mail|#formulario|outline', align: 'center' }),
      ]),
    ],
  },
  {
    key: 'contato.unidades',
    category: 'contato',
    label: 'Unidades de atendimento',
    description: 'Vários endereços em colunas.',
    preview: 'grid-3',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '1120px', pattern: 'grid', motion: 'fade-up' }, [
        contato(
          'São Paulo|Av. Paulista, 1000\nRio de Janeiro|Av. Rio Branco, 250\nBelo Horizonte|Av. Afonso Pena, 800',
          '3',
          'Nossas unidades',
        ),
      ]),
    ],
  },
  {
    key: 'contato.compacto',
    category: 'contato',
    label: 'Contato compacto',
    description: 'Faixa curta com telefone e e-mail.',
    preview: 'grid-2',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'md', align: 'center', maxWidth: '820px', backgroundColor: '#f8fafc', radius: 'xl' }, [
        contato('Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br', '2'),
      ]),
    ],
  },
  {
    key: 'contato.com-horarios',
    category: 'contato',
    label: 'Contato e horários',
    description: 'Dados de contato acompanhados dos horários.',
    preview: 'grid-3',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'lg', align: 'center', maxWidth: '1120px', motion: 'fade' }, [
        contato(DADOS, '3', 'Fale com a gente'),
        contato(HORARIOS, '3', 'Horário de atendimento'),
      ]),
    ],
  },
  {
    key: 'contato.sobre-foto',
    category: 'contato',
    label: 'Contato sobre a foto',
    description: 'Foto de fundo escurecida com os dados por cima.',
    preview: 'banner',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { bleed: 'contained', backgroundImage: FOTO.cidadeNoite, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '1000px', motion: 'fade' },
        [b('ui.title', { text: 'Estamos por perto', size: 'lg', level: 'h2', align: 'center' }), contato(DADOS)],
      ),
    ],
  },
  {
    key: 'contato.completo',
    category: 'contato',
    label: 'Contato completo',
    description: 'Título, dados, horários e convite final.',
    preview: 'grid-3',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { bleed: 'contained', paddingY: 'xl', align: 'center', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Atendimento em todos os canais', size: 'lg', level: 'h2', align: 'center' }),
        contato(DADOS),
        contato(HORARIOS),
        b('ui.buttons', { items: 'Falar com o time|#formulario|solid', align: 'center' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
